// NOTE: NSAI-6584 — one shared contract for the global search palette on web
// and mobile. Server-produced item types mirror EGlobalSearchItemType in
// apps/nsai/server/src/global-search/types/global-search.types.ts value for
// value; the client-only types below them are built on the device from data
// the client already has (static page index, project list, FAQ, …).
export enum EGlobalSearchItemType {
  // NOTE: Server — GET /search/global
  ASSISTANT = "assistant",
  PROMPT = "prompt",
  CUSTOM_TOOL = "custom_tool",
  CUSTOM_INTEGRATION = "custom_integration",
  INTEGRATION_CONNECTION = "integration_connection",
  SKILL = "skill",
  CATALOG_SKILL = "catalog_skill",
  AUTOMATION = "automation",
  MARKETPLACE_AUTOMATION = "marketplace_automation",
  MODEL = "model",
  KNOWLEDGE_FILE = "knowledge_file",
  MEETING = "meeting",
  CALENDAR_EVENT = "calendar_event",
  GENERATED_FILE = "generated_file",
  DOC_RECOGNITION_DOCUMENT = "doc_recognition_document",
  CHAT_FOLDER = "chat_folder",
  MEMBER = "member",
  INVITE = "invite",
  ROLE = "role",
  API_KEY = "api_key",
  MEMORY_FACT = "memory_fact",
  CUSTOM_INSTRUCTIONS = "custom_instructions",
  PROBE = "probe",
  // NOTE: Client — the existing chat endpoints (keyword + semantic), mapped
  // into the same item shape by the palette.
  CHAT = "chat",
  CHAT_MESSAGE = "chat_message",
  // NOTE: Client — built on the device.
  PAGE = "page",
  SETTINGS_TAB = "settings_tab",
  SETTINGS_OPTION = "settings_option",
  INTEGRATION_PROVIDER = "integration_provider",
  PROJECT = "project",
  ORGANIZATION = "organization",
  FAQ_ITEM = "faq_item",
  NOTIFICATION = "notification",
  ACTION = "action",
}

export interface IGlobalSearchItem {
  type: EGlobalSearchItemType;
  id: string;
  title: string;
  subtitle: string | null;
  // NOTE: Navigation identifiers only, string-valued. The keys each type
  // carries are listed on getGlobalSearchItemTarget.
  params: Record<string, string>;
}

export interface IGlobalSearchGroup {
  type: EGlobalSearchItemType;
  items: IGlobalSearchItem[];
}

export interface IGlobalSearchResponse {
  groups: IGlobalSearchGroup[];
}

export interface ISearchGlobalParams {
  query: string;
  limit?: number;
  // NOTE: NSAI-6853 — every source's default listing; `query` is ignored.
  browse?: boolean;
}

// NOTE: Where a result takes the user, independent of platform. Web resolves a
// target to a react-router path, mobile to an expo-router route; the server
// and this package never build either.
export enum EGlobalSearchTargetKind {
  NEW_CHAT = "new_chat",
  CHAT = "chat",
  NEW_CHAT_IN_FOLDER = "new_chat_in_folder",
  CHAT_FOLDERS = "chat_folders",
  ASSISTANTS = "assistants",
  ASSISTANT = "assistant",
  PROMPT_LIBRARY = "prompt_library",
  INTEGRATIONS = "integrations",
  INTEGRATION_DETAILS = "integration_details",
  CUSTOM_INTEGRATION = "custom_integration",
  SKILLS = "skills",
  SKILL = "skill",
  SKILL_CATALOG = "skill_catalog",
  SKILL_CATALOG_ENTRY = "skill_catalog_entry",
  AGENTS = "agents",
  MODELS = "models",
  KNOWLEDGE = "knowledge",
  CALL_ASSISTANT = "call_assistant",
  MEETING = "meeting",
  UPLOADS = "uploads",
  GENERATED_FILES = "generated_files",
  HTML_PREVIEW = "html_preview",
  DOC_RECOGNITION = "doc_recognition",
  DOC_RECOGNITION_DOCUMENT = "doc_recognition_document",
  ANALYTICS = "analytics",
  ANONYMIZATION_LAB = "anonymization_lab",
  SETTINGS = "settings",
  FAQ = "faq",
  // NOTE: Actions — not a navigation. The platform performs them.
  SWITCH_PROJECT = "switch_project",
  OPEN_NOTIFICATION = "open_notification",
  SEARCH_HISTORY = "search_history",
  SEARCH_DOCS = "search_docs",
}

export interface IGlobalSearchTarget {
  kind: EGlobalSearchTargetKind;
  params: Record<string, string>;
}

// NOTE: Gate on a client-side entry (page, settings tab, settings option).
// Each platform answers it from the same permission / flag / role state its
// own navigation already uses, so an entry is shown exactly when the page it
// opens would be. The gate values are defined next to the static index.
export type TGlobalSearchGateResolver = (gate: string) => boolean;

export interface IGlobalSearchStaticEntry {
  id: string;
  type:
    | EGlobalSearchItemType.PAGE
    | EGlobalSearchItemType.SETTINGS_TAB
    | EGlobalSearchItemType.SETTINGS_OPTION;
  // NOTE: Fully-qualified i18n keys ("namespace:path") so both platforms
  // translate them with their own `t` without knowing the namespace upfront.
  titleKey: string;
  subtitleKey?: string;
  // NOTE: NSAI-6792 — the settings area a settings option lives in ("Profile
  // settings", "Organization"). Several tabs share their name across both
  // areas (Token & Cost Usage, API Keys), so the option's subtitle names the
  // area too. Shown, never matched: "profile" must not pull in every option.
  sectionKey?: string;
  // NOTE: Extra words a user might type for this entry (synonyms), as i18n
  // keys whose values may hold several comma-separated terms per locale.
  keywordKeys?: string[];
  gates: string[];
  target: IGlobalSearchTarget;
}

export type TGlobalSearchTranslate = (key: string) => string;

export interface IScoreGlobalSearchMatchProps {
  query: string;
  candidates: readonly (string | null | undefined)[];
}

export interface ISearchGlobalSearchStaticEntriesProps {
  query: string;
  entries: readonly IGlobalSearchStaticEntry[];
  translate: TGlobalSearchTranslate;
  isGateOpen: TGlobalSearchGateResolver;
  limit: number;
}
